// KPI presentation config for the client portal. Pure data.
// Contracts: public-kpis.md (GET public/stats?view=kpi) + public-client-channel.md
// (GET public/donors/:id) — the keys mirror the payload verbatim. The backend
// owns the numbers and each rate's `qualifier` (good|regular|bad); the UI never
// recomputes thresholds — it only maps keys to label and color.

// production{} → counter cards. `key` must match the payload; the renderer
// FILTERS to the keys present (donors_kpi omite crias/losses; oocytes/viables
// van en el embudo, no como tarjetas — kpi-catalog G1). `icon`/`accent` =
// paridad visual con el dashboard (features/kpis/layouts/production.jsx).
export const PRODUCTION_CARDS = [
    { key: 'opu_sessions', label: 'Sesiones OPU',          icon: 'briefcase', accent: 'accent' },
    { key: 'aspirations',  label: 'Aspiraciones',          icon: 'droplet',   accent: 'accent' },
    { key: 'clivages',     label: 'Clivages',              icon: 'sperm',     accent: 'primary' },
    { key: 'embryos',      label: 'Embriones',             icon: 'cell',      accent: 'primary' },
    { key: 'transfers',    label: 'Transferencias',        icon: 'lineage',   accent: 'accent' },
    { key: 'pregnancies',  label: 'Preñeces',              icon: 'animal',    accent: 'primary' },
    { key: 'births',       label: 'Partos',                icon: 'hospital',  accent: 'primary' },
    { key: 'crias',        label: 'Crías',                 icon: 'animal',    accent: 'accent' },
    { key: 'losses',       label: 'Pérdidas embrionarias', icon: 'close',     accent: 'danger' },
];

// recipients_kpi.production{} + el stats de la receptora (view=full). Labels
// compartidos por parcerias/detail e informes (2º consumidor → constante).
export const RECIPIENT_PRODUCTION_CARDS = [
    { key: 'transfers',   label: 'Transferencias', icon: 'lineage',  accent: 'accent' },
    { key: 'pregnancies', label: 'Preñeces',       icon: 'animal',   accent: 'primary' },
    { key: 'confirmed',   label: 'Confirmadas',    icon: 'check',    accent: 'primary' },
    { key: 'lost',        label: 'Pérdidas',       icon: 'close',    accent: 'danger' },
    { key: 'born',        label: 'Crías',          icon: 'hospital', accent: 'primary' },
];

// funnel.stages[] arrives ordered; keys cover both the client-wide funnel
// (admin-kpis) and the per-recipient funnel (admin-recipients ?view=full).
export const FUNNEL_LABELS = {
    ovocitos:     'Ovocitos',
    viables:      'Viables',
    embriones:    'Embriones',
    transferidos: 'Transferidos',
    preneces:     'Preñeces',
    confirmadas:  'Confirmadas',
    partos:       'Partos',
};

// Rampa secuencial MONÓTONA sky → teal → verde (etapa temprana → tardía),
// portada del dashboard (core/constants/kpis.js): interpola los anclajes de
// marca --color-accent (#3bb5ed) → --color-primary (#04c2a0) → verde profundo.
// Es DATA de gráfico (color por dato, como el ancho de barra), no CSS de tema.
export const FUNNEL_COLORS = {
    ovocitos:     '#3bb5ed',
    viables:      '#25bace',
    embriones:    '#0fbfaf',
    transferidos: '#04b391',
    confirmadas:  '#049674',
    preneces:     '#049674',
    partos:       '#047857',
};

// view=rates → rates{}. `late` = mature-cohort rate (carries cohort_n).
// `inverted` = lower is better. Color comes from the backend qualifier.
export const RATE_ROWS = [
    { key: 'viability',      label: 'Viabilidad' },
    { key: 'fertilization',  label: 'Fertilización' },
    { key: 'transfer',       label: 'Transferencia' },
    { key: 'pregnancy',      label: 'Preñez',                late: true },
    { key: 'survival',       label: 'Supervivencia',         late: true },
    { key: 'embryonic_loss', label: 'Pérdidas embrionarias', late: true, inverted: true },
];

// Recipient dashboard exposes only the carrier-side rates (decisión 0041).
export const RECIPIENT_RATE_ROWS = [
    { key: 'pregnancy', label: 'Preñez',        late: true },
    { key: 'survival',  label: 'Supervivencia', late: true },
];

// Below this cohort size the rate is statistically noisy — the UI surfaces
// the counts as the honest signal (0041: "los counts son la señal honesta").
export const LOW_COHORT_N = 5;

// ── Cowhousing (donadoras, rol donor_owner) ─────────────────────────────
// Catálogo del dominio en docs/reference/cowhousing-kpi-catalog.md; DTOs en
// src/features/vacas/queries/types.js (contract: public-client-channel.md).
//
// Genético (G1–G4): la vista de donadora REUSA las constantes de arriba —
// GET public/donors/:id → data.rates trae exactamente las keys de RATE_ROWS
// (6/6) y data.funnel usa FUNNEL_LABELS. PRODUCTION_CARDS es un subconjunto
// DELIBERADO de data.production (11 claves): omite oocytes y viables, que se
// presentan en el embudo y no como tarjetas (kpi-catalog G1). Sin labels nuevos.

// Económico disponible (E1–E3), en orden de presentación. El backend entrega
// estos montos ya derivados (0047 §6 — regla de dinero: solo derivaciones
// propias del cliente); el front no recalcula, solo formatea con fmtMoney.
export const DONOR_ECONOMIC_ROWS = [
    { key: 'gross_value',      label: 'Valor de la vaca',          money: true },
    { key: 'stake_value',      label: 'Tu participación',          money: true },
    { key: 'offspring_return', label: 'Retorno por crías (bruto)', money: true },
];

// Última liquidación mensual (D-009, GET public/liquidations →
// items[].last_liquidation). Presentación de la card del detalle: el backend
// entrega el reparto CONGELADO (0055) — day_rate × días × % del snapshot; el
// front solo formatea. «Tu %» = percent_snapshot (el % con que se repartió ese
// mes) ≠ «Tu participación» percent (co-propiedad actual, ya renderizada).
export const LIQUIDATION_ROWS = [
    { key: 'days',              label: 'Días',        fmt: 'num' },
    { key: 'day_rate_snapshot', label: 'Tarifa/día',  fmt: 'money' },
    { key: 'pension_amount',    label: 'Pensión del mes', fmt: 'money' },
    { key: 'percent_snapshot',  label: 'Tu % del reparto', fmt: 'pct',   share: true },
    { key: 'share_amount',      label: 'Tu reparto',  fmt: 'money', share: true, highlight: true },
];

// Económico NO disponible aún (E6–E8: estado de cuenta [0056] y ROI neto —
// sin canal público). `liquidation`/`share` salieron de aquí en D-009: ya son
// datos reales (GET public/liquidations). Marcador para que la UI muestre
// "próximamente" sin cablear nada: sin fetchers, keys ni paths.
export const PENDING_ECONOMIC_KPIS = ['account_statement', 'net_roi'];
